import $ from 'jquery';
import 'cropperjs/dist/cropper.min.css';
import Cropper from 'cropperjs';
import { triggerStateChangedRequest } from '~core';
import { hideAllMenus } from '~core/hideAllMenus';
import { getState } from '~src/state';
import { toggleEditorSidebar } from '~src/sidebarLegacy';
import { v4 as uuidv4 } from 'uuid';
import { changeImageSrc, getUrlExtension } from '~src/utils';
import { onErrorShowMessage } from '~src/errors';
import { trackEvent } from '~src/tracking';
import {
  handleTabAttachments,
  displaySidebarAttachments,
  fetchEditorAttachments,
} from './sidebarLegacy';
import toggleSaveNavMenu from '~core/navbar/toggleSaveNavMenu';
import { prepareImageForEditing } from '~core/image';
import { getGlobalControlsManager } from '~core/customControls';

let cropper;
let croppingShape = 'rect';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getRoundedCanvas = () => {
  const croppedCanvas = cropper.getCroppedCanvas({
    fillColor: 'rgba(255, 255, 255, 0)',
  });
  var canvas = document.createElement('canvas');
  var context = canvas.getContext('2d');
  var width = croppedCanvas.width;
  var height = croppedCanvas.height;

  canvas.width = width;
  canvas.height = height;
  context.imageSmoothingEnabled = true;
  context.drawImage(croppedCanvas, 0, 0, width, height);
  context.globalCompositeOperation = 'destination-in';
  context.beginPath();
  context.arc(
    width / 2,
    height / 2,
    Math.min(width, height) / 2,
    0,
    2 * Math.PI,
    true
  );
  context.fill();
  return canvas;
};

const getEllipseCanvas = () => {
  const croppedCanvas = cropper.getCroppedCanvas({
    fillColor: 'rgba(255, 255, 255, 0)',
  });
  var canvas = document.createElement('canvas');
  var context = canvas.getContext('2d');
  var width = croppedCanvas.width;
  var height = croppedCanvas.height;
  canvas.width = width;
  canvas.height = height;
  context.imageSmoothingEnabled = true;
  context.drawImage(croppedCanvas, 0, 0, width, height);
  context.globalCompositeOperation = 'destination-in';
  context.beginPath();
  context.ellipse(
    width / 2,
    height / 2,
    width / 2,
    height / 2,
    0 * Math.PI,
    0,
    45 * Math.PI
  );
  context.fill();
  return canvas;
};

const stopCropping = () => {
  cropper.destroy();
  hideAllMenus();
  toggleSaveNavMenu();
  $('#cropper').fadeOut();
  $('#editor-menu-images-cropping-commands').hide();
  $('#canvas-container').show();
  // Hide header toolbar container after cropping ends
  $('.editor-menu-container').hide();

  // Exit cropping visual state and restore floating controls
  $('body').removeClass('cropping-active');
  try {
    const cm = getGlobalControlsManager();
    const sel = window.canvas && window.canvas.getActiveObject();
    if (cm && sel) {
      // If selection is MagicFrame child, show parent control
      const target = sel._magicFrame ? sel._magicFrame : sel;
      cm.showControl(target);
    }
  } catch (e) {
    console.error('Error restoring controls after cropping', e);
  }

  setTimeout(() => {
    $('#modalCropper').modal('hide');
    $('.modal-backdrop').remove();
  }, 200);

  $('#btn-modal-cropper-use-image').text('Conferma').removeAttr('disabled');
  $('#btn-modal-cropper-cancel').removeAttr('disabled');
  $('#editor-zoom-controls').show();
};

const getCurrentImageContentType = () => {
  if (['ellipse', 'circle'].includes(croppingShape)) {
    return 'image/png';
  }

  const selection = window.canvas.getActiveObject();
  const ext = getUrlExtension(selection.src || selection._element.currentSrc);
  let contentType = 'image/jpeg';
  if (ext === 'png') {
    contentType = 'image/png';
  }
  return contentType;
};

const toBlobPromise = (canvas) => {
  return new Promise((resolve) => {
    return canvas.toBlob(
      (blob) => {
        resolve(blob);
      },
      'image/png',
      0.9
    );
  });
};

const useCroppedImage = async () => {
  let blob;

  if (croppingShape === 'ellipse') {
    blob = await toBlobPromise(getEllipseCanvas());
  } else if (croppingShape === 'circle') {
    blob = await toBlobPromise(getRoundedCanvas());
  } else {
    blob = await toBlobPromise(cropper.getCroppedCanvas());
  }

  $('#btn-modal-cropper-use-image')
    .text('Salvataggio in corso...')
    .attr('disabled', 'disabled');
  $('#btn-modal-cropper-cancel').attr('disabled', 'disabled');

  const attachmentsUploader = getState('uploader.allegati');

  try {
    const filename = `cropped-${uuidv4()}.png`;

    attachmentsUploader.addFile({
      name: filename,
      type: blob.type,
      data: blob,
    });

    const response = await attachmentsUploader.upload();

    attachmentsUploader.cancelAll();

    const url = `${process.env.EDGE_RESIZER}${
      new URL(response.successful[0].uploadURL).pathname
    }`;

    const selection = window.canvas.getActiveObject();
    let magicFrame = null;

    // Check if the selection is a MagicFrame or if it's the person image of a MagicFrame
    if (selection.type === 'MagicFrame') {
      magicFrame = selection;
    } else if (selection._magicFrame) {
      magicFrame = selection._magicFrame;
    }

    if (magicFrame) {
      await magicFrame.updatePerson(url);
      magicFrame.scalePersonToWidth(magicFrame.width * magicFrame.scaleX);
      magicFrame.scalePersonToWidth(magicFrame.width * magicFrame.scaleX);
    } else {
      const selection = window.canvas.getActiveObject();
      const isErased = typeof selection.eraser !== 'undefined';
      if (isErased) {
        selection.set('eraser', undefined);
      }
      await changeImageSrc(window.canvas.getActiveObject(), url);
    }

    window.canvas.renderAll();
    triggerStateChangedRequest();
    stopCropping();

    const { attachments } = await fetchEditorAttachments(true);
    window.editorConfig.sidebarAttachments = attachments;
    displaySidebarAttachments();
    handleTabAttachments();
  } catch (err) {
    console.error(err);
    onErrorShowMessage();
    $('#btn-modal-cropper-use-image').text('Conferma').removeAttr('disabled');
    $('#btn-modal-cropper-cancel').removeAttr('disabled');
    $('#editor-zoom-controls').show();
  }
};

const applyCrop = () => {
  const contentType = getCurrentImageContentType();
  const selection = window.canvas.getActiveObject();
  selection.filters = [];
  selection.flipX = false;
  selection.flipY = false;
  let base64;

  if (croppingShape === 'ellipse') {
    base64 = getEllipseCanvas().toDataURL(contentType);
  } else if (croppingShape === 'circle') {
    base64 = getRoundedCanvas().toDataURL(contentType);
  } else {
    base64 = cropper.getCroppedCanvas().toDataURL(contentType);
  }

  $('#modalCropper').modal('show');

  document
    .getElementById('cropping-preview')
    .setAttribute('src', `data:${base64}`);

  trackEvent('Crop', {
    shape: croppingShape,
  });
};

const startCropping = async () => {
  const selection = window.canvas.getActiveObject();

  // If this is a MagicFrame, prepare the person image instead
  let imageToEdit = selection;
  if (selection.type === 'MagicFrame' && selection.editingImg) {
    imageToEdit = selection.editingImg;
  }

  const imageUrl = await prepareImageForEditing(imageToEdit);
  if (!imageUrl) {
    console.error('No image URL found in current selection', selection);
    return;
  }

  hideAllMenus();
  toggleSaveNavMenu();
  $('#editor-zoom-controls').hide();
  // Enter cropping state: hide any floating controls and block their display
  try {
    const cm = getGlobalControlsManager();
    cm && cm.hideAllControls();
  } catch (e) {
    console.error('Error hiding controls during crop start', e);
  }
  $('body').addClass('cropping-active');
  // Show header toolbar container during cropping
  $('.editor-menu-container').show();
  if (getState('isSidebarOpen')) {
    toggleEditorSidebar();
  }

  await sleep(500);
  croppingShape = 'rect';
  $('#cropper').removeClass('rounded');
  $('#canvas-container').fadeOut();
  $('#editor-menu-images-cropping-commands').show();
  $('#cropper').show();
  $('#editor-menu-crop').show();
  // TODO: lock sidebar & navbar

  const image = document.getElementById('image-to-crop');
  image.src = imageUrl;

  const options = {
    viewMode: 1,
    zoomOnWheel: false,
    zoomOnTouch: false,
    ready: function () {
      setTimeout(() => {
        this.cropper.zoomTo(0);
      }, 100);
      window.cropper = cropper;
    },
  };
  cropper = new Cropper(image, options);
};

const cropZoomIn = () => {
  cropper.zoom(0.1);
};

const cropZoomOut = () => {
  cropper.zoom(-0.1);
};

const cropShapeAspectRatio = (value) => () => {
  $('#cropper').removeClass('rounded');
  croppingShape = 'rect';
  cropper.setAspectRatio(value);
};

const cropShapeEllipse = () => {
  croppingShape = 'ellipse';
  cropper.setAspectRatio(0);
  $('#cropper').addClass('rounded');
};

const cropShapeCircle = () => {
  croppingShape = 'circle';
  cropper.setAspectRatio(1);
  $('#cropper').addClass('rounded');
};

export const initCropListeners = () => {
  $('body').on('click', '#editor-menu-image-crop', startCropping);
  $('body').on('click', '#editor-menu-crop-apply', applyCrop);
  $('body').on('click', '#editor-menu-crop-cancel', stopCropping);
  $('body').on('click', '#btn-modal-cropper-use-image', useCroppedImage);
  $('body').on('click', '#editor-menu-crop-zoom-in', cropZoomIn);
  $('body').on('click', '#editor-menu-crop-zoom-out', cropZoomOut);
  $('body').on('click', '#editor-menu-crop-zoom-ellipse', cropShapeEllipse);
  $('body').on('click', '#editor-menu-crop-zoom-circle', cropShapeCircle);
  $('body').on(
    'click',
    '#editor-menu-crop-zoom-rect',
    cropShapeAspectRatio(9 / 16)
  );
  $('body').on(
    'click',
    '#editor-menu-crop-zoom-square',
    cropShapeAspectRatio(1)
  );
  $('body').on('click', '#editor-menu-crop-zoom-free', cropShapeAspectRatio(0));
};
